import api from './api';
import logger from '../utils/logger';

// Tipo para os dados de atualização do usuário
export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  oab?: string;
  estado_oab?: string;
  cpf_cnpj?: string;
  officeAddress?: string;
}

// Tipo para resposta do usuário
export interface UserResponse {
  id: string;
  email: string;
  nome_completo: string;
  verificado: boolean;
  numero_oab?: string;
  estado_oab?: string;
  plano: string;
  creditos_disponiveis: number;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  phone?: string;
  oab?: string;
  cpf_cnpj?: string;
  office_address?: string;

  created_at: string;
  updated_at: string;
}

/**
 * Mapear dados do usuário da API para o formato do frontend
 */
export const mapUserFromApi = (apiUser: any): UserResponse => {
  logger.debug('Mapping user from API:', apiUser);

  // Extrair firstName e lastName do first_name/last_name da API ou nome_completo
  let firstName = apiUser.first_name || '';
  let lastName = apiUser.last_name || '';

  if ((!firstName || !lastName) && apiUser.nome_completo) {
    const nameParts = apiUser.nome_completo.split(' ');
    firstName = firstName || nameParts[0] || '';
    lastName = lastName || nameParts.slice(1).join(' ') || '';
  }

  // Construir nome completo se não existir
  const nomeCompleto = apiUser.nome_completo || `${firstName} ${lastName}`.trim();

  // Use API data as the primary source of truth
  const mappedUser = {
    id: apiUser.id || '',
    email: apiUser.email || '',
    nome_completo: nomeCompleto,
    verificado: apiUser.is_verified || false,
    numero_oab: apiUser.oab_number || apiUser.numero_oab || null,
    estado_oab: apiUser.estado_oab || null,
    plano: apiUser.plan || 'basic',
    creditos_disponiveis: apiUser.available_credits || 0,
    // Map backend fields to frontend fields correctly
    firstName: apiUser.first_name || '',
    lastName: apiUser.last_name || '',
    first_name: apiUser.first_name || '',  // Keep both for compatibility
    last_name: apiUser.last_name || '',    // Keep both for compatibility
    avatar: apiUser.avatar_url ? `https://api.advogadaparceira.com.br${apiUser.avatar_url}` : undefined,
    avatar_url: apiUser.avatar_url ? `https://api.advogadaparceira.com.br${apiUser.avatar_url}` : undefined,  // Keep both for compatibility
    phone: apiUser.phone || '',
    oab: apiUser.oab_number || '',
    oab_number: apiUser.oab_number || '',  // Keep both for compatibility
    cpf_cnpj: apiUser.cpf_cnpj || '',
    office_address: apiUser.office_address || '',
    created_at: apiUser.created_at || '',
    updated_at: apiUser.updated_at || '',
    // Add required User interface fields
    role: apiUser.role || 'user',
    is_admin: apiUser.is_admin || false,
    plan: apiUser.plan || 'basic',
    is_active: apiUser.is_active || true,
    is_verified: apiUser.is_verified || false,
    available_credits: apiUser.available_credits || 0,
    // Onboarding fields
    onboarding_completed_at: apiUser.onboarding_completed_at || null,
    onboarding_version: apiUser.onboarding_version || 0
  };

  logger.debug('Mapped user for frontend:', mappedUser);
  return mappedUser;
};

/**
 * Obter dados do usuário atual
 */
export const getCurrentUser = async (): Promise<UserResponse> => {
  const response = await api.get('/users/me');
  return mapUserFromApi(response.data);
};

/**
 * Atualizar dados do perfil do usuário
 */
export const updateUserProfile = async (userData: UpdateUserData): Promise<UserResponse> => {
  // Criar uma versão modificada do objeto userData para a API
  const apiUserData: any = {};

  // Se firstName e lastName estiverem presentes, combinar em nome_completo
  if (userData.firstName !== undefined || userData.lastName !== undefined) {
    // Garantir que pegamos os valores atuais se algum estiver indefinido
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const firstName = userData.firstName !== undefined ? userData.firstName : currentUser.firstName || '';
    const lastName = userData.lastName !== undefined ? userData.lastName : currentUser.lastName || '';

    // Combinar firstName e lastName em nome_completo
    apiUserData.nome_completo = `${firstName} ${lastName}`.trim();
  }

  // Mapear campos do frontend para o backend
  if (userData.email !== undefined) apiUserData.email = userData.email;
  if (userData.phone !== undefined) apiUserData.phone = userData.phone;
  if (userData.oab !== undefined) apiUserData.numero_oab = userData.oab;
  if (userData.estado_oab !== undefined) apiUserData.estado_oab = userData.estado_oab;
  if (userData.cpf_cnpj !== undefined) apiUserData.cpf_cnpj = userData.cpf_cnpj;
  if (userData.officeAddress !== undefined) apiUserData.office_address = userData.officeAddress;



  // Enviar para a API
  const response = await api.put('/users/me', apiUserData);



  // Mapear a resposta para o formato do frontend
  return mapUserFromApi(response.data);
};

/**
 * Fazer upload de avatar do usuário
 */
export const uploadAvatar = async (file: File): Promise<UserResponse> => {
  try {
    // Criar uma FormData para o upload
    const formData = new FormData();
    formData.append('file', file);

    // Usar fetch diretamente para evitar problemas com FormData
    const token = localStorage.getItem('token');
    const baseURL = import.meta.env.VITE_API_URL || '/api/v1';

    const response = await fetch(`${baseURL}/users/me/avatar`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    const data = await response.json();
    console.log('Avatar upload response:', data);
    return mapUserFromApi(data);
  } catch (error) {
    console.error('Erro ao fazer upload do avatar:', error);

    // Fallback: salvar o avatar localmente se o backend falhar
    return saveAvatarLocally(file);
  }
};

/**
 * Salvar avatar localmente (fallback se o backend falhar)
 */
const saveAvatarLocally = async (file: File): Promise<UserResponse> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      if (event.target && event.target.result) {
        // Salvar o avatar como base64 no localStorage
        const avatarDataUrl = event.target.result.toString();
        localStorage.setItem('userAvatar', avatarDataUrl);

        // Obter dados atuais do usuário
        const userProfile = localStorage.getItem('userProfile');
        let userData: any = {};

        if (userProfile) {
          try {
            userData = JSON.parse(userProfile);
          } catch (e) {
            console.error('Erro ao analisar perfil do usuário:', e);
          }
        }

        // Buscar o usuário atual e mapear com o novo avatar
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

        // Adicionar o avatar aos dados do usuário
        const updatedUser = {
          ...currentUser,
          avatar: avatarDataUrl,
          ...userData
        };

        // Armazenar usuário atualizado
        localStorage.setItem('currentUser', JSON.stringify(updatedUser));

        // Retornar usuário mapeado com o novo avatar
        resolve(mapUserFromApi({
          ...currentUser,
          avatar: avatarDataUrl
        }));
      }
    };

    // Ler o arquivo como uma URL de dados
    reader.readAsDataURL(file);
  });
};

/**
 * Atualizar senha do usuário
 */
export const updatePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  await api.put('/users/me/password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
};



/**
 * Remover avatar do usuário
 */
export const deleteAvatar = async (): Promise<{ message: string }> => {
  try {
    const response = await api.delete('/users/me/avatar');
    return response.data;
  } catch (error) {
    console.error('Erro ao remover avatar:', error);

    // Fallback: remover avatar localmente se o backend falhar
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const updatedUser = { ...currentUser, avatar: null };
    localStorage.setItem('currentUser', JSON.stringify(updatedUser));

    return { message: "Avatar removido localmente" };
  }
};

/**
 * Marcar onboarding como completo
 */
export const completeOnboarding = async (version: number = 1): Promise<UserResponse> => {
  const response = await api.patch('/users/me/onboarding', {
    completed: true,
    version
  });
  return mapUserFromApi(response.data);
};

/**
 * Resetar onboarding de um usuário (admin only)
 */
export const resetUserOnboarding = async (userId: string): Promise<{ message: string }> => {
  const response = await api.post(`/users/admin/${userId}/reset-onboarding`);
  return response.data;
};

/**
 * Resetar próprio onboarding (para rever o tour)
 */
export const resetMyOnboarding = async (): Promise<UserResponse> => {
  const response = await api.patch('/users/me/onboarding', {
    completed: false,
    version: 0
  });
  return mapUserFromApi(response.data);
}; 